import React, { useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { useCart } from '../context/CartContext';
import Modal from './Modal';
import { CartLine, formatMoney } from './Shop';
import './comic.css';

// iOS-readiness: inside the native Capacitor shell, a plain <a href> would
// navigate Shopify's hosted checkout INTO the app's own WebView, replacing
// the whole app (losing all React/session state) rather than feeling like a
// native checkout hand-off. Browser.open() instead presents it in an
// SFSafariViewController -- a system-provided in-app browser sheet that
// slides over the app and can be dismissed back to it, the standard pattern
// for "external payment page from within a native app." On plain web this
// is a no-op detour: Capacitor.isNativePlatform() is false, so the click
// falls through to the <a>'s own normal navigation, unchanged from before.
const handleCheckoutClick = (checkoutUrl) => (e) => {
  if (Capacitor.isNativePlatform()) {
    e.preventDefault();
    Browser.open({ url: checkoutUrl });
  }
};

// Fixed-position cart button that stays on-screen regardless of scroll
// position (position: fixed, rendered once at the app root -- see App.js),
// replacing the old cart summary that used to sit inline at the top of the
// Shop panel. Hidden entirely while the cart is empty, matching the prior
// behavior of only showing cart UI once there's something in it.
const FloatingCart = () => {
  const { cart } = useCart();
  const [open, setOpen] = useState(false);

  if (!cart || cart.lines.length === 0) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={`Open cart, ${cart.totalQuantity} item${cart.totalQuantity === 1 ? "" : "s"}`}
        className="comic-floating-cart"
      >
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="9" cy="21" r="1.5" />
          <circle cx="19" cy="21" r="1.5" />
          <path d="M2 3h2l2.4 12.2a2 2 0 0 0 2 1.6h8.6a2 2 0 0 0 2-1.6L21 7H6" />
        </svg>
        <span className="comic-floating-cart__badge">{cart.totalQuantity}</span>
      </button>

      {open && (
        <Modal title="Your Cart" onClose={() => setOpen(false)}>
          {cart.lines.map((line) => (
            <CartLine key={line.id} line={line} />
          ))}
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: "8px", fontFamily: "'Anonymous Pro', monospace", fontWeight: "bold", color: "#232526" }}>
            <span>Subtotal</span>
            <span>{formatMoney(cart.cost.subtotalAmount.amount, cart.cost.subtotalAmount.currencyCode)}</span>
          </div>
          <a
            href={cart.checkoutUrl}
            onClick={handleCheckoutClick(cart.checkoutUrl)}
            className="comic-btn comic-btn--end"
            style={{ marginTop: "10px", display: "block", textAlign: "center", textDecoration: "none" }}
          >
            Checkout
          </a>
        </Modal>
      )}
    </>
  );
};

export default FloatingCart;
